import { BaseAction } from '../../store/actions';
import { ChatInfo, ChatMessage, ChatConnectOptions } from '../../shared/models/chat';

export enum ChatActions {
  ConnectChat = '[Chat] Connect',
  ChatConnected = '[Chat] Connected',
  ChatConnectFailed = '[Chat] Connect Failed',
  DisconnectChat = '[Chat] Disconnect',
  ChatStateChanged = '[Chat] State Changed',
  SubscribeChannel = '[Chat] Subscribe Channel',
  UnsubscribeChannel = '[Chat] Unsubscribe Channel',
  SyncChatChannels = '[Chat] Sync Channels',
  SyncChatChannelsSuccess = '[Chat] Sync Channels Success',
  SyncChatChannelsFailed = '[Chat] Sync Channels Failed',
  ChannelSubscriptionFailed = '[Chat] Channel Subscription Failed',
  ChatReconnectScheduled = '[Chat] Reconnect Scheduled',
  ChatReconnectAttemptsExhausted = '[Chat] Reconnect Attempts Exhausted',
  MessageReceived = '[Chat] Message Received',
  LoadMessages = '[Chat] Load Messages',
  LoadMessagesSuccess = '[Chat] Load Messages Success',
  LoadMessagesFailed = '[Chat] Load Messages Failed',
  SendMessage = '[Chat] Send Message',
  SendMessageSuccess = '[Chat] Send Message Success',
  SendMessageFailed = '[Chat] Send Message Failed',
  ChatNewMessageUpdate = '[Chat] Chat New Message Update',
  ReadMessages = '[Chat] Read Messages',
  ReadAllChats = '[Chat] Read All Chats'
}

export class ConnectChat implements BaseAction<ChatActions> {
  readonly type = ChatActions.ConnectChat;

  constructor(public readonly payload?: ChatConnectOptions) { }
}

export class ChatConnected implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatConnected;

  constructor(public readonly payload?: any) { }
}

export class ChatConnectFailed implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatConnectFailed;

  constructor(public readonly payload: string) { }
}

export class DisconnectChat implements BaseAction<ChatActions> {
  readonly type = ChatActions.DisconnectChat;
}

export class ChatStateChanged implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatStateChanged;

  constructor(public readonly payload: { previous: string, current: string }) { }
}

export class SubscribeChannel implements BaseAction<ChatActions> {
  readonly type = ChatActions.SubscribeChannel;

  constructor(public readonly payload: {
    chat_channel: string,
    chatInfo: ChatInfo
  }) {}
}

export class UnsubscribeChannel implements BaseAction<ChatActions> {
  readonly type = ChatActions.UnsubscribeChannel;

  constructor(public readonly payload: string) {}
}

// Re-fetches the org's chat channels (and seeds their message history) so
// subscriptions stay current as tasks are created/completed during a
// session, independently of the one-time connect flow. Dispatched once on
// connect and again in the background on each page navigation.
export class SyncChatChannels implements BaseAction<ChatActions> {
  readonly type = ChatActions.SyncChatChannels;
}

export class SyncChatChannelsSuccess implements BaseAction<ChatActions> {
  readonly type = ChatActions.SyncChatChannelsSuccess;
}

export class SyncChatChannelsFailed implements BaseAction<ChatActions> {
  readonly type = ChatActions.SyncChatChannelsFailed;

  constructor(public readonly payload: string) {}
}

export class ChannelSubscriptionFailed implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChannelSubscriptionFailed;

  constructor(public readonly payload: {
    chat_channel: string,
    error: string
  }) {}
}

// Dispatched each time a reconnect attempt is scheduled after a failed
// connect, so the UI can reflect "reconnecting" state and the attempt count.
export class ChatReconnectScheduled implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatReconnectScheduled;

  constructor(public readonly payload: {
    attempt: number,
    delay: number
  }) {}
}

export class ChatReconnectAttemptsExhausted implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatReconnectAttemptsExhausted;
}

export class MessageReceived implements BaseAction<ChatActions> {
  readonly type = ChatActions.MessageReceived;

  constructor(public readonly payload: {
    chat_channel: string,
    message: ChatMessage
  }) {}
}

export class LoadMessages implements BaseAction<ChatActions> {
  readonly type = ChatActions.LoadMessages;

  constructor(public readonly payload: string) {}
}

export class LoadMessagesSuccess implements BaseAction<ChatActions> {
  readonly type = ChatActions.LoadMessagesSuccess;

  constructor(public readonly payload: {
    chat_channel: string,
    messages: ChatMessage[]
  }) {}
}

export class LoadMessagesFailed implements BaseAction<ChatActions> {
  readonly type = ChatActions.LoadMessagesFailed;

  constructor(public readonly payload: {
    error: string,
    chat_channel: string
  }) {}
}

export class SendMessage implements BaseAction<ChatActions> {
  readonly type = ChatActions.SendMessage;

  constructor(public readonly payload: {
    chat_channel: string,
    message: ChatMessage,
    invite_code: string
  }) {}
}

export class SendMessageSuccess implements BaseAction<ChatActions> {
  readonly type = ChatActions.SendMessageSuccess;

  constructor(public readonly payload: {
    chat_channel: string,
    message: ChatMessage
  }) {}
}

export class SendMessageFailed implements BaseAction<ChatActions> {
  readonly type = ChatActions.SendMessageFailed;

  constructor(public readonly payload: {
    error: string,
    chat_channel: string
  }) {}
}

export class ChatNewMessageUpdate implements BaseAction<ChatActions> {
  readonly type = ChatActions.ChatNewMessageUpdate;

  constructor(public readonly payload: {
    chat_channel: string,
    message: string
  }) {}
}

export class ReadMessages implements BaseAction<ChatActions> {
  readonly type = ChatActions.ReadMessages;

  constructor(public readonly payload: string) {}
}

export class ReadAllChats implements BaseAction<ChatActions> {
  readonly type = ChatActions.ReadAllChats;

}



export type ChatAction = ConnectChat | ChatConnected | ChatConnectFailed | DisconnectChat | ChatStateChanged
  | SubscribeChannel| UnsubscribeChannel | SyncChatChannels | SyncChatChannelsSuccess | SyncChatChannelsFailed
  | ChannelSubscriptionFailed | ChatReconnectScheduled | ChatReconnectAttemptsExhausted
  | MessageReceived | LoadMessages | LoadMessagesSuccess | LoadMessagesFailed
  | SendMessage | SendMessageSuccess | SendMessageFailed | ChatNewMessageUpdate | ReadMessages | ReadAllChats;
